const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export type TextTone = 'light' | 'dark';

export const isHexColor = (value: string | null | undefined): value is string =>
  typeof value === 'string' && HEX_COLOR.test(value);

const srgbToLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

const parseHex = (hex: string): { r: number; g: number; b: number } => {
  const digits = hex.replace('#', '');
  const h = digits.length === 3 ? [...digits].map((digit) => digit + digit).join('') : digits;
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
};

/**
 * WCAG relative luminance of a 3- or 6-digit hex colour.
 */
export const relativeLuminance = (hex: string): number => {
  const { r, g, b } = parseHex(hex);
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
};

export const wcagContrastRatio = (hex1: string, hex2: string): number => {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * HSL lightness (0 black – 1 white) of a 3- or 6-digit hex colour.
 */
export const hslLightness = (hex: string): number => {
  const { r, g, b } = parseHex(hex);
  return (Math.max(r, g, b) + Math.min(r, g, b)) / 2;
};

export interface TextToneRule {
  /** HSL lightness at or above which text turns dark. */
  darkTextMinLightness: number;
  /** Light text colour, checked against `minLightTextContrast`. */
  lightText: string;
  /** WCAG contrast below which light text is treated as unreadable and text turns dark. */
  minLightTextContrast: number;
}

/**
 * Dark text on light backgrounds by HSL lightness, and on any background where light text
 * would fall below the minimum contrast (bright yellows, limes and cyans); light text otherwise.
 */
export const textToneForBackground = (
  background: string,
  { darkTextMinLightness, lightText, minLightTextContrast }: TextToneRule
): TextTone =>
  hslLightness(background) >= darkTextMinLightness || wcagContrastRatio(background, lightText) < minLightTextContrast
    ? 'dark'
    : 'light';
